import type { Ref } from "react";
import { useMemo, useRef } from "react";
import type { DropDownProps } from "../../DropDown";
import DropDown, { useDropDown } from "../../DropDown";
import type { TextFieldProps } from "../TextField";
import TextField from "../TextField";
import styles from "./ComboBox.module.scss";
import { ChevronTop, Delete } from "@monta/icons";
import clsx from "clsx";
import type { ListProps, TValidListValues } from "../../List";
import List, { filterListProps } from "../../List";
import { useControlledValueState } from "../../../utils/hooks/state";

export type ComboBoxProps<T extends TValidListValues> = Omit<
  TextFieldProps,
  "value" | "onChange"
> &
  ListProps<T> & {
    dropDownProps?: DropDownProps;
    loading?: boolean;
  };

function ComboBox<T extends TValidListValues | TValidListValues[] = string>(
  props: ComboBoxProps<T>,
  ref: Ref<HTMLInputElement>,
) {
  const [listProps, restProps] = filterListProps<T, ComboBoxProps<T>>(props);
  const { dropDownProps, ...textFieldProps } = restProps;

  const [value, setValue] = useControlledValueState(
    listProps.value as any,
    undefined as any,
    listProps.onChange as any,
  );

  const selectedItem = useMemo(
    () =>
      Array.isArray(value)
        ? value.map((i) => listProps.items.find((j) => i == j.value))
        : listProps.items.find((i) => i.value == value),
    [value, listProps.items],
  );

  const dropDownController = useDropDown();

  const clearButtonRef = useRef<HTMLDivElement>(null);

  const currentValue = Array.isArray(selectedItem)
    ? selectedItem.map((i) => i?.label).join("، ")
    : selectedItem?.label || "";

  return (
    <DropDown
      {...dropDownProps}
      {...dropDownController}
      button={
        <TextField
          {...textFieldProps}
          loading={listProps.loading}
          value={currentValue}
          readOnly
          style={{ cursor: "pointer" }}
          ref={ref}
          innerafter={
            <div className={styles["combobox__actions"]}>
              {currentValue && (
                <div
                  ref={clearButtonRef}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setValue(listProps.multiple ? [] : undefined);
                  }}
                  className={styles["combobox__delete"]}
                >
                  <Delete size="small" />
                </div>
              )}
              <ChevronTop
                className={styles["combobox__chevron"]}
                size="small"
              />
            </div>
          }
          wrapperClassName={clsx(
            styles["combobox"],
            dropDownController.isOpen && styles["combobox--active"],
          )}
        />
      }
      ignoreComponents={[clearButtonRef]}
      sameWidthAsInput
      stayOpenOnClick={!listProps.multiple}
      disableYOverflow
    >
      <List
        {...listProps}
        value={value}
        onChange={(e: any) => {
          setValue(e);
          if (!listProps.multiple) dropDownController.close();
        }}
        className={styles["combobox__list"]}
      />
    </DropDown>
  );
}

export default ComboBox;
